import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import AlertBox from '@/components/layout/AlertBox';
import Botao from '@/components/Botao';
import Icon from '@mdi/react';
import { mdiFilter, mdiLanConnect, mdiLanPending, mdiLoading, mdiMenuDown, mdiMenuUp, mdiPause, mdiPlay } from '@mdi/js';
import { useAppliance } from '@/lib/useAppliance';
import { extractApiError } from '@/lib/apiError';
import type { ConexaoAtivaDTO } from '@webip/shared/src/dto/conexoes';
import { corDoLink, formatarBytes, formatarTaxa, nomeDoLink, ordenar, useConexoes } from './comum';
import styles from './conexoes.module.css';

type Filtro = { origem: string; origemPorta: string; destino: string; destinoPorta: string };

const FILTRO_VAZIO: Filtro = { origem: '', origemPorta: '', destino: '', destinoPorta: '' };

type Coluna = 'status' | 'proto' | 'origem' | 'destino' | 'link' | 'up' | 'down' | 'upTotal' | 'downTotal';

const LIMITES = [100, 250, 500, 1000];

function filtroDaUrl(params: URLSearchParams): Filtro {
    return {
        origem: params.get('origem') ?? '',
        origemPorta: params.get('origem_porta') ?? '',
        destino: params.get('destino') ?? '',
        destinoPorta: params.get('destino_porta') ?? '',
    };
}

/** IP por prefixo ("10.101.250.") ou nome do dispositivo/redirecionamento por trecho. */
function casaEndereco(filtro: string, ip: string | undefined, nomes: (string | undefined)[]): boolean {
    if (!filtro) return true;
    const f = filtro.trim().toLowerCase();
    if (ip?.startsWith(f)) return true;
    return nomes.some((n) => n?.toLowerCase().includes(f));
}

function casaPorta(filtro: string, porta: number | undefined): boolean {
    return !filtro || String(porta ?? '') === filtro.trim();
}

const chaves: Record<Coluna, (c: ConexaoAtivaDTO) => string | number> = {
    status: (c) => c.estado ?? '',
    proto: (c) => c.protocolo,
    origem: (c) => (c.direcao === 'saida' && c.dispositivo_nome) || c.origem_ip,
    destino: (c) => c.destino_ip,
    link: (c) => c.wan_id ?? -1,
    up: (c) => c.upload_bps,
    down: (c) => c.download_bps,
    upTotal: (c) => c.upload_bytes,
    downTotal: (c) => c.download_bytes,
};

export default function ConexoesPage() {
    const { applianceId, selectedAppliance } = useAppliance();
    const [params, setParams] = useSearchParams();
    const [executando, setExecutando] = useState(true);
    const [limite, setLimite] = useState(100);
    const [ordem, setOrdem] = useState<{ coluna: Coluna; desc: boolean }>({ coluna: 'origem', desc: false });
    const [rascunho, setRascunho] = useState<Filtro>(() => filtroDaUrl(params));
    const filtro = useMemo(() => filtroDaUrl(params), [params]);

    // Link vindo da tela de tráfego (?origem=…) troca o filtro aplicado.
    useEffect(() => setRascunho(filtro), [filtro]);

    const query = useConexoes(applianceId, executando);
    const dados = query.data;

    const aplicar = (f: Filtro = rascunho) => {
        const p = new URLSearchParams();
        if (f.origem) p.set('origem', f.origem.trim());
        if (f.origemPorta) p.set('origem_porta', f.origemPorta.trim());
        if (f.destino) p.set('destino', f.destino.trim());
        if (f.destinoPorta) p.set('destino_porta', f.destinoPorta.trim());
        setParams(p, { replace: true });
    };

    const filtradas = useMemo(() => {
        const lista = (dados?.conexoes ?? []).filter((c) =>
            casaEndereco(filtro.origem, c.origem_ip, c.direcao === 'saida' ? [c.dispositivo_nome] : [])
            && casaPorta(filtro.origemPorta, c.origem_porta)
            && casaEndereco(filtro.destino, c.destino_ip, [c.redirecionamento_nome, c.interno_ip])
            && casaPorta(filtro.destinoPorta, c.destino_porta));
        return ordenar(lista, chaves[ordem.coluna], ordem.desc);
    }, [dados, filtro, ordem]);

    const visiveis = filtradas.slice(0, limite);

    const cabecalho = (coluna: Coluna, rotulo: string, num = false) => (
        <th
            className={`${styles.ordenavel} ${num ? styles.num : ''}`}
            onClick={() => setOrdem((o) => ({
                coluna,
                // Taxas e totais começam do maior; textos, em ordem alfabética.
                desc: o.coluna === coluna ? !o.desc : num,
            }))}
        >
            {rotulo}
            {ordem.coluna === coluna && <Icon path={ordem.desc ? mdiMenuDown : mdiMenuUp} size={0.7} style={{ verticalAlign: -4 }} />}
        </th>
    );

    const preencher = (campo: keyof Filtro, valor: string | number | undefined) => {
        if (valor == null || valor === '') return;
        setRascunho((r) => ({ ...r, [campo]: String(valor) }));
    };

    const erro = query.error ? extractApiError(query.error, 'Não foi possível consultar as conexões').message : null;

    return (
        <LayoutUsuario>
            <VBox className={styles.root} style={{ gap: '16px', padding: '16px' }}>
                <PageHeader
                    title={`Conexões ativas${selectedAppliance ? ` — ${selectedAppliance.nome}` : ''}`}
                    description="Conexões que estão trafegando agora — as paradas não são listadas. Taxas atuais e totais desde o início de cada conexão."
                    actions={applianceId ? (
                        <Botao
                            variant="outline-secondary"
                            icon={executando ? mdiPause : mdiPlay}
                            onClick={() => setExecutando((v) => !v)}
                        >
                            {executando ? 'Executando' : 'Pausado'}
                        </Botao>
                    ) : undefined}
                />

                {!applianceId && (
                    <p style={{ color: 'var(--color-muted)', fontStyle: 'italic' }}>
                        Selecione um appliance no menu lateral.
                    </p>
                )}

                {applianceId && (
                    <>
                        <form
                            className={styles.toolbar}
                            onSubmit={(e) => { e.preventDefault(); aplicar(); }}
                        >
                            {(['origem', 'destino'] as const).map((lado) => {
                                const portaCampo = lado === 'origem' ? 'origemPorta' : 'destinoPorta';
                                return (
                                    <div key={lado} className={styles.filtro}>
                                        <span className={styles.filtroRotulo}>{lado === 'origem' ? 'Origem' : 'Destino'}</span>
                                        <div className={styles.filtroCampos}>
                                            <input
                                                aria-label={`IP ou nome de ${lado}`}
                                                placeholder="IP ou nome"
                                                value={rascunho[lado]}
                                                onChange={(e) => setRascunho((r) => ({ ...r, [lado]: e.target.value }))}
                                            />
                                            <span className={styles.filtroSep}>:</span>
                                            <input
                                                aria-label={`Porta de ${lado}`}
                                                placeholder="Porta"
                                                inputMode="numeric"
                                                value={rascunho[portaCampo]}
                                                onChange={(e) => setRascunho((r) => ({ ...r, [portaCampo]: e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                            <HBox style={{ gap: '8px' }}>
                                <Botao type="submit" variant="primary" icon={mdiFilter}>Aplicar</Botao>
                                {Object.values(filtro).some(Boolean) && (
                                    <Botao
                                        variant="outline-secondary"
                                        onClick={() => { setRascunho(FILTRO_VAZIO); aplicar(FILTRO_VAZIO); }}
                                    >
                                        Limpar
                                    </Botao>
                                )}
                            </HBox>
                        </form>

                        <HBox style={{ gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <select
                                className={styles.select}
                                aria-label="Quantidade de linhas"
                                value={limite}
                                onChange={(e) => setLimite(Number(e.target.value))}
                            >
                                {LIMITES.map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                            {dados && (
                                <span className={styles.resumo}>
                                    <strong>{visiveis.length} de {filtradas.length} registros.</strong>{' '}
                                    Clique numa origem, destino ou porta e em Aplicar para filtrar.
                                    {dados.total > dados.conexoes.length && (
                                        <> Mostrando as {dados.conexoes.length} conexões de maior taxa de {dados.total}.</>
                                    )}
                                </span>
                            )}
                            {query.isFetching && <Icon path={mdiLoading} size={0.8} spin className={styles.muted} />}
                            {dados && (
                                <span className={styles.coletado}>
                                    Atualizado às {new Date(dados.coletado_em).toLocaleTimeString('pt-BR')}
                                </span>
                            )}
                        </HBox>

                        {erro && <AlertBox variant="danger">{erro}</AlertBox>}

                        {query.isLoading && (
                            <HBox style={{ justifyContent: 'center', padding: '32px' }}>
                                <Icon path={mdiLoading} size={1.5} spin />
                            </HBox>
                        )}

                        {dados && (
                            <div className={styles.tabelaWrap}>
                                <table className={styles.tabela}>
                                    <thead>
                                        <tr>
                                            <th className={styles.grupo} colSpan={5} />
                                            <th className={`${styles.grupo} ${styles.num}`} colSpan={2}>Taxa atual</th>
                                            <th className={`${styles.grupo} ${styles.num}`} colSpan={2}>Total</th>
                                        </tr>
                                        <tr>
                                            {cabecalho('status', 'Status')}
                                            {cabecalho('proto', 'Proto')}
                                            {cabecalho('origem', 'Origem')}
                                            {cabecalho('destino', 'Destino')}
                                            {cabecalho('link', 'Link')}
                                            {cabecalho('up', 'Upload', true)}
                                            {cabecalho('down', 'Download', true)}
                                            {cabecalho('upTotal', 'Upload', true)}
                                            {cabecalho('downTotal', 'Download', true)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visiveis.map((c) => (
                                            <LinhaConexao
                                                key={c.id || `${c.protocolo}-${c.origem_ip}:${c.origem_porta}-${c.destino_ip}:${c.destino_porta}`}
                                                c={c}
                                                corLink={corDoLink(dados.links, c.wan_id)}
                                                nomeLink={nomeDoLink(dados.links, c.wan_id)}
                                                preencher={preencher}
                                            />
                                        ))}
                                        {visiveis.length === 0 && (
                                            <tr><td colSpan={9} className={styles.vazio}>Nenhuma conexão trafegando{Object.values(filtro).some(Boolean) ? ' com esse filtro' : ''}.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </VBox>
        </LayoutUsuario>
    );
}

type LinhaProps = {
    c: ConexaoAtivaDTO;
    corLink: string;
    nomeLink: string;
    preencher: (campo: keyof Filtro, valor: string | number | undefined) => void;
};

function LinhaConexao({ c, corLink, nomeLink, preencher }: LinhaProps) {
    const estabelecida = c.estado === 'established';
    const nomeOrigem = c.direcao === 'saida' ? c.dispositivo_nome : undefined;

    return (
        <tr>
            <td>
                {c.estado && (
                    <span className={styles.estado} title={`TCP: ${c.estado}`}>
                        <Icon
                            path={estabelecida ? mdiLanConnect : mdiLanPending}
                            size={0.75}
                            color={estabelecida ? 'var(--status-online)' : 'var(--color-muted)'}
                        />
                    </span>
                )}
            </td>
            <td>{c.protocolo}</td>
            <td>
                <span className={styles.clicavel} title={c.origem_ip} onClick={() => preencher('origem', c.origem_ip)}>
                    {nomeOrigem ?? c.origem_ip}
                </span>
                {c.origem_porta ? (
                    <span className={`${styles.porta} ${styles.clicavel}`} onClick={() => preencher('origemPorta', c.origem_porta)}>
                        {' '}:{c.origem_porta}
                    </span>
                ) : null}
            </td>
            <td>
                <span className={styles.clicavel} onClick={() => preencher('destino', c.destino_ip)}>{c.destino_ip}</span>
                {c.destino_porta ? (
                    <span className={`${styles.porta} ${styles.clicavel}`} onClick={() => preencher('destinoPorta', c.destino_porta)}>
                        {' '}:{c.destino_porta}
                    </span>
                ) : null}
                {c.redirecionada && (
                    <span className={styles.nomeSecundario} title={`Redirecionada para ${c.interno_ip}:${c.interno_porta ?? ''}`}>
                        → {c.redirecionamento_nome ?? c.dispositivo_nome ?? c.interno_ip}
                    </span>
                )}
            </td>
            <td>
                {nomeLink && (
                    <span className={styles.linkChip}>
                        <span className={styles.linkSwatch} style={{ background: corLink }} />
                        {nomeLink}
                        {c.direcao === 'entrada' && <span className={styles.muted}>(entrada)</span>}
                    </span>
                )}
            </td>
            <td className={styles.num}>{formatarTaxa(c.upload_bps)}</td>
            <td className={styles.num}>{formatarTaxa(c.download_bps)}</td>
            <td className={styles.num}>{formatarBytes(c.upload_bytes)}</td>
            <td className={styles.num}>{formatarBytes(c.download_bytes)}</td>
        </tr>
    );
}
